import React, {
  memo, useCallback, useEffect, useRef, useState,
} from 'react'

import { classnames, Icon, Search } from '@cgtrader/triangle-ui/dist/src/ssr-index'

import FilterSearchInput from 'Pods/SearchPage/Components/Shared/FilterSearchInput/FilterSearchInput'
import { EVENT_SOURCE, FILTER_NAME } from 'Pods/SearchPage/Shared/Tracking/constants'
import { FilterTrackingProvider, useTrackFilterSelect } from 'Pods/SearchPage/Shared/Tracking/FilterTracking'
import useTranslate from 'Pods/SearchPage/Shared/useTranslate'
import { useSearchPageStore } from 'Pods/SearchPage/Store'
import { cancelKeywordCommit, commitKeyword } from 'Pods/SearchPage/Store/actions/keyword'

import Panel from './Components/Panel/Panel'

const PANEL_ID = 'search-within-results-panel'

// From lg the input sits in place; below it an icon opens the input inside the panel. Both render on
// the server and CSS picks one, so the bar never shifts on hydration.
function SearchWithinResults() {
  const translate = useTranslate()

  const keyword = useSearchPageStore(state => state.keyword)
  // Clears usually write the same '' the store already holds, so `keyword` alone would not rerender
  // this component and the input would keep showing a term that is no longer applied. The counter
  // changes on every clear (Store/slices/keyword.js), which is what makes the resync fire.
  const keywordClearCount = useSearchPageStore(state => state.keywordClearCount)
  const hasTags = useSearchPageStore(state => state.tags.length > 0)
  const [value, setValue] = useState(keyword)
  const [isOpen, setIsOpen] = useState(false)
  const trackFilterSelect = useTrackFilterSelect()
  // The text typed here, so the commit it produces is reported once and external writes are not.
  const typedKeywordRef = useRef(null)

  const rootRef = useRef(null)
  const panelRef = useRef(null)
  const triggerRef = useRef(null)
  const inlineInputRef = useRef(null)
  const panelInputRef = useRef(null)
  // Escape hands focus back to the inline input, whose focus handler would reopen the panel.
  const skipFocusOpenRef = useRef(false)

  useEffect(() => {
    if (keyword && keyword === typedKeywordRef.current) {
      typedKeywordRef.current = null
      trackFilterSelect(FILTER_NAME.WITHIN_KEYWORDS, keyword.trim())
    }
  }, [keyword, trackFilterSelect])

  // External store writes (Reset, Clear all filters, popstate restore) must win: sync the input and
  // drop any pending commit so stale text cannot resurrect the value the external write just cleared.
  useEffect(() => {
    cancelKeywordCommit()
    setValue(keyword)
  }, [keyword, keywordClearCount])

  useEffect(() => cancelKeywordCommit, [])

  useEffect(() => {
    if (!isOpen) return undefined

    // offsetParent is null while CSS hides the panel's own input (from lg, where the inline input
    // already holds focus).
    if (panelInputRef.current?.offsetParent) panelInputRef.current.focus()

    const handleOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) setIsOpen(false)
    }
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return
      // Closing unmounts the panel, which would drop keyboard focus to <body>; focus can also already
      // be there when the focused chip was just removed from the panel.
      const { activeElement } = document
      if (panelRef.current?.contains(activeElement) || activeElement === document.body) {
        if (triggerRef.current?.offsetParent) {
          triggerRef.current.focus()
        } else {
          skipFocusOpenRef.current = true
          inlineInputRef.current?.focus()
        }
      }
      setIsOpen(false)
    }

    document.addEventListener('mousedown', handleOutside)
    document.addEventListener('focusin', handleOutside)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handleOutside)
      document.removeEventListener('focusin', handleOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  const handleChange = (nextValue) => {
    setValue(nextValue)
    typedKeywordRef.current = nextValue
    commitKeyword(nextValue)
  }

  const handleFieldFocus = useCallback(() => {
    if (skipFocusOpenRef.current) {
      skipFocusOpenRef.current = false
      return
    }
    setIsOpen(true)
  }, [])

  const handleFieldClick = useCallback(() => setIsOpen(true), [])

  // Selecting or removing a term unmounts the button that held focus, so hand it to the visible
  // search input instead of letting it fall to <body>.
  const handleTermChange = useCallback(() => {
    const input = panelInputRef.current?.offsetParent ? panelInputRef.current : inlineInputRef.current
    input?.focus()
  }, [])
  const handleToggle = useCallback(() => setIsOpen(open => !open), [])

  const label = translate('search_page.filters.search_within.button_label')
  const placeholder = translate('search_page.filters.secondary.search_placeholder')

  return (
    <div ref={rootRef} className="search-within">
      <div
        className="search-within__field"
        onFocus={handleFieldFocus}
        onClick={handleFieldClick}
        role="presentation"
      >
        <FilterSearchInput
          id="search-within-results"
          className="search-within__input"
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          ariaLabel={label}
          inputRef={inlineInputRef}
        />
      </div>
      <button
        ref={triggerRef}
        type="button"
        className={classnames(
          'cgt-text-input cgt-text-input--medium cgt-text-input--primary filter-wrapper__trigger',
          'search-within__trigger',
          (keyword !== '' || hasTags) && 'filter-wrapper__trigger--filled',
        )}
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls={isOpen ? PANEL_ID : undefined}
        onClick={handleToggle}
      >
        <Icon notSpaced svgComponent={Search} size="small" color="darker" className="search-within__trigger-icon" />
      </button>
      {isOpen && (
        <div ref={panelRef} id={PANEL_ID} className="search-within__panel" role="dialog" aria-label={label}>
          <div className="search-within__panel-input">
            <FilterSearchInput
              id="search-within-results-compact"
              className="search-within__input"
              value={value}
              onChange={handleChange}
              placeholder={placeholder}
              ariaLabel={label}
              inputRef={panelInputRef}
            />
          </div>
          <FilterTrackingProvider eventSource={EVENT_SOURCE.TAGS}>
            <Panel onTermChange={handleTermChange} />
          </FilterTrackingProvider>
        </div>
      )}
    </div>
  )
}

export default memo(SearchWithinResults)
